// FontAwesome to Lucide icon mapping
// This file maps FontAwesome icons used across all apps to their Lucide equivalents

import {
  Plus,
  CreditCard,
  AlignJustify,
  ChevronDown,
  ChevronUp,
  Archive,
  ArrowDown,
  ArrowDownToLine,
  ArrowLeft,
  ArrowRight,
  ArrowLeftRight,
  ArrowUp,
  RotateCcw,
  Move,
  ArrowUpDown,
  ExternalLink,
  Atom,
  Backpack,
  Ban,
  Bug,
  Menu,
  Filter,
  Bell,
  HelpCircle,
  Book,
  Bookmark,
  Globe,
  Calendar,
  ShoppingCart as ShoppingCartPlus,
  ShoppingCart,
  TrendingUp,
  BarChart3,
  TreePine,
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Circle,
  CheckCircle2,
  Dot,
  AlertCircle,
  AtSign,
  Play,
  X,
  Clock,
  Copy,
  Upload,
  Download,
  Save,
  Edit,
  Trash,
  Eye,
  EyeOff,
  Home,
  User,
  File,
  Folder,
  Image,
  Video,
  Pause,
  Volume2,
  Star,
  Share,
  Search,
  Settings,
  Lock,
  Unlock,
  Activity,
  Gauge,
  Sun,
  Moon,
  ZoomIn,
  ZoomOut,
  Sliders,
  SlidersHorizontal,
  GripVertical,
  Minus,
  PlusCircle,
  MinusCircle,
  Square,
  Maximize,
  Minimize,
  Layers,
  CheckSquare,
  Ruler,
  Link,
  Compass,
  Grid3X3,
  AlertTriangle,
  FolderUp,
  Zap,
  Hexagon,
  Dice6,
  Box,
  type LucideIcon,
  List,
  FolderDown,
  PlayCircle,
  XCircle,
  MapPin,
  Share2,
  Crosshair,
  ArrowUpCircle,
  DollarSign,
  Target,
  Camera,
  LogOut,
  Subtitles,
  Trash2,
  PersonStanding,
  MoreVertical,
  MessageSquare,
  Hand,
  Send,
  Users,
  Monitor,
  MicOff,
  VideoOff,
  Car,
  Film,
  Heart,
  Lightbulb

} from 'lucide-react';

// FontAwesome to Lucide mapping object
export const fontAwesomeToLucideMap: Record<string, LucideIcon> = {
  // Basic actions
  faAdd: Plus,
  faPlus: Plus,
  faSolidPlus: Plus,
  faMinus: Minus,
  faCheck: Check,
  faX: X,
  faXmark: X, // X mark (close icon)
  faTimes: X,
  faClose: X,
  
  // Navigation
  faArrowLeft: ArrowLeft,
  faArrowRight: ArrowRight,
  faArrowUp: ArrowUp,
  faArrowDown: ArrowDown,
  faArrowUpRightFromSquare: ExternalLink,
  faArrowDownToLine: ArrowDownToLine,
  faChevronLeft: ChevronLeft,
  faChevronRight: ChevronRight,
  faChevronDown: ChevronDown,
  faChevronUp: ChevronUp,
  faAngleDown: ChevronDown,
  faAngleUp: ChevronUp,
  faCaretDown: ChevronDown,
  faCaretUp: ChevronUp,
  faCaretCircleDown: ChevronDown,
  faChevronCircleDown: ChevronDown,
  
  // UI Elements
  faMenu: Menu,
  faBars: Menu,
  faFilter: Filter,
  faSearch: Search,
  faSettings: Settings,
  faCog: Settings,
  faGear: Settings,
  faBell: Bell,
  faQuestion: HelpCircle,
  faQuestionCircle: HelpCircle,
  faInfo: AlertCircle,
  faInfoCircle: AlertCircle,
  faExclamation: AlertTriangle,
  faExclamationTriangle: AlertTriangle,
  faTriangleExclamation: AlertTriangle,
  
  // Media and files
  faPlay: Play,
  faPause: Pause,
  faStop: Square,
  faVolume: Volume2,
  faVolumeUp: Volume2,
  faImage: Image,
  faVideo: Video,
  faFile: File,
  faFolder: Folder,
  faDownload: Download,
  faUpload: Upload,
  faSave: Save,
  faCopy: Copy,
  
  // User and social
  faUser: User,
  faHome: Home,
  faShare: Share,
  faStar: Star,
  faHeart: Star, // Using Star as fallback for Heart
  faThumbsUp: Star, // Using Star as fallback
  
  // Actions
  faEdit: Edit,
  faPencil: Edit,
  faTrash: Trash,
  faDelete: Trash,
  faEye: Eye,
  faEyeSlash: EyeOff,
  faLock: Lock,
  faUnlock: Unlock,
  
  // Business and commerce
  faShoppingCart: ShoppingCart,
  faCartPlus: ShoppingCartPlus,
  faCreditCard: CreditCard,
  faCalendar: Calendar,
  faBook: Book,
  faBookmark: Bookmark,
  faTag: Star, // Using Star as fallback
  faTags: Star, // Using Star as fallback
  
  // Technology
  faCode: Edit, // Using Edit as fallback for Code
  faGlobe: Globe,
  faBrowser: Globe,
  faAtom: Atom,
  faBug: Bug,
  
  // Layout and positioning
  faAlignJustify: AlignJustify,
  faAlignLeft: AlignJustify,
  faAlignCenter: AlignJustify,
  faAlignRight: AlignJustify,
  faMove: Move,
  faArrowsUpDownLeftRight: Move,
  faResize: Move,
  faGripVertical: GripVertical,
  faExpand: Maximize,
  faCompress: Minimize,
  faMaximize: Maximize,
  faMinimize: Minimize,
  
  // Shapes and drawing
  faSquare: Square,
  faCircle: Circle,
  faPolygon: Hexagon,
  faDrawPolygon: Hexagon,
  
  // Miscellaneous
  faActivity: Activity,
  faGauge: Gauge,
  faTachometer: Gauge,
  faSun: Sun,
  faMoon: Moon,
  faZoomIn: ZoomIn,
  faZoomOut: ZoomOut,
  faSliders: Sliders,
  faSlidersH: SlidersHorizontal,
  faLayers: Layers,
  faLayerGroup: Layers,
  faRuler: Ruler,
  faLink: Link,
  faCompass: Compass,
  faGrid2: Grid3X3,
  faArchive: Archive,
  faBackpack: Backpack,
  faBan: Ban,
  faClock: Clock,
  faTime: Clock,
  faTreePine: TreePine,
  faTree: TreePine,
  faRotateLeft: RotateCcw,
  faUndo: RotateCcw,
  faArrowLeftRight: ArrowLeftRight,
  faArrowUpDown: ArrowUpDown,
  faTrendingUp: TrendingUp,
  faChart: BarChart3,
  faBarChart: BarChart3,
  faCheckCircle: CheckCircle,
  faCheckCircle2: CheckCircle2,
  faCheckSquare: CheckSquare,
  faSquareCheck: CheckSquare,
  faSquareXmark: X,
  faPlusCircle: PlusCircle,
  faMinusCircle: MinusCircle,
  faCirclePlus: PlusCircle,
  faCircleMinus: MinusCircle,
  faDot: Dot,
  faAt: AtSign,
  faFolderArrowUp: FolderUp,
  faFolderUp: FolderUp,
  faBolt: Zap,
  faDiceD6: Dice6,
  faMinusOctagon: Minus,
  faMinusSquare: Square,
  faPlusSquare: Square,
  faCirclePlay: Play,

    // Additional eCommerce specific icons
    faCartCircleArrowDown: ChevronDown,
    faCartCircleArrowUp: ChevronUp,
    faVrCardboard: Box, // Using Box as placeholder for VR cardboard
  
    // Additional icons found in public app
    faPlayCircle: PlayCircle,

    faXCircle: XCircle,
    faAlertCircle: AlertCircle,

    faExclamationCircle: AlertCircle,
  
    // Loading and progress
    faSpinner: RotateCcw, // Using RotateCcw for spinner
    faCircleNotch: RotateCcw,
  
    // Additional navigation
    faAngleDoubleDown: ChevronDown,
    faAngleLeft: ChevronLeft,
    faAngleRight: ChevronRight,
  
    // Cloud and download
    faCloudDownloadAlt: Download,
    faCloudDownload: Download,
  
    // Location and maps
    faMapMarkerAlt: MapPin,
    faMapMarker: MapPin,
  
    // Interaction
    faHandPointer: Hand, // Need to import Hand from lucide
  
    // Real estate specific
    faBed: Home, // Using Home as placeholder for bed
    faShower: Home, // Using Home as placeholder for shower
    faCar: Car, // Need to import Car from lucide
    faRulerCombined: Ruler,
    faMoneyBillWave: DollarSign,
  
    // Social media
    faFacebook: Share2, // Using Share2 as placeholder for Facebook
    faTwitter: Share2, // Using Share2 as placeholder for Twitter
    faWhatsapp: Share2, // Using Share2 as placeholder for WhatsApp
  
    // Additional icons from BottomBar
    faMusic: Volume2, // Using Volume2 as placeholder for music
    faPauseCircle: Pause, // Pause circle
    faWalking: PersonStanding, // Using PersonStanding for walking
    faCouch: Home, // Using Home as placeholder for couch
    faCube: Box, // Using Box for cube
    faMapMarked: MapPin, // Map with marker
    faHouse: Home, // House icon
    faLanguage: Globe, // Using Globe for language
    faComments: MessageSquare, // Multiple comments
  
    // Additional icons from ClassicPublicAsset
    faBath: Home, // Using Home as placeholder for bath
    faGlobeAmericas: Globe, // Globe Americas
    faInstagram: Share2, // Using Share2 as placeholder for Instagram
    faLinkedin: Share2, // Using Share2 as placeholder for LinkedIn
  
    // Additional icons from video Toolbar
    faVideoSlash: VideoOff, // Video off/slash
    faMicrophoneSlash: MicOff, // Microphone off/slash
    faDesktop: Monitor, // Desktop/monitor
    faUserGroup: Users, // User group (using Users)
    faEllipsisVertical: MoreVertical, // Vertical ellipsis
  
    // Additional icons from TourConfigs
    faClosedCaptioning: Subtitles, // Closed captioning/subtitles
    faLongArrowAltRight: ArrowRight, // Long arrow right
  
    // Additional icons from NoteDescriptionField
    faCircleExclamation: AlertCircle, // Circle with exclamation
    faCircleXmark: XCircle, // Circle with X mark
    faPaperPlaneTop: Send, // Paper plane top (using Send)
  
    // Additional icons from Minimap
    faBullseye: Target, // Bullseye/target icon
    faCameraWeb: Camera, // Web camera (using Camera)
    faSearchPlus: ZoomIn, // Search plus (zoom in)
    faSearchMinus: ZoomOut, // Search minus (zoom out)
    faArrowAltCircleUp: ArrowUpCircle, // Arrow alt circle up
  
  
    // Additional icons from Notes
    faLocationCrosshairs: Crosshair, // Location crosshairs
    faXmarkLarge: X, // Large X mark (using X)
  
    // Additional icons from VolumeControls
  
    // Additional icons from FloorPlans
    faFileArrowDown: Download, // File arrow down (download)
  
    // Additional icons from Gallery
    faFolderArrowDown: FolderDown, // Folder arrow down
  
    // Additional icons from Navigation components
    faXTwitter: X, // X (formerly Twitter)
  
    // Additional icons from Navigation search components
    faArrowUpArrowDown: ArrowUpDown, // Arrow up arrow down (sort)
  
    // Additional icons from Notes components
    faListUl: List, // List with bullets
  
    // Additional icons from Notes card actions
    faArrowRotateLeft: RotateCcw, // Arrow rotate left (counter-clockwise)
    faPenToSquare: Edit, // Pen to square (edit)
    faTrashCan: Trash2, // Trash can
    faArrowRightFromBracket: LogOut, // Arrow right from bracket (logout)
  
    // Additional icons from eCommerce Store
    faSortDown: ChevronDown, // Sort down (using ChevronDown)
  
    // Additional icons from Avatar IntroPopup
    faSquareFull: Square, // Square full (filled square)
  
    // Additional social media icons from eCommerce About
    faFacebookF: Share2, // Facebook F (using Share2 as placeholder)
    faLinkedinIn: Share2, // LinkedIn In (using Share2 as placeholder)
  
    // Additional icons from FlowsMenu
    faAtomSimple: Atom, // Atom simple
  
    // Additional icons from SortMenu
    faBarsFilter: SlidersHorizontal, // Bars filter (using SlidersHorizontal)
  
    // Additional icons from GuideModal
    faRunning: Zap, // Running (using Zap as placeholder for activity)
  
    // Additional icons from Avatar components
    faArrowLeftLong: ArrowLeft // Long arrow left (using ArrowLeft)
};

// Helper function to get Lucide icon from FontAwesome icon name
export function getLucideIcon(fontAwesomeIconName: string): LucideIcon | undefined {
  return fontAwesomeToLucideMap[fontAwesomeIconName];
}

// Export commonly used Lucide icons for direct import
export {
  Plus,
  Minus,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Menu,
  Settings,
  Search,
  Edit,
  Trash,
  Eye,
  EyeOff,
  Home,
  User,
  Bell,
  Calendar,
  File,
  Folder,
  Image,
  Video,
  Play,
  Pause,
  Volume2,
  Star,
  Share,
  Download,
  Upload,
  Copy,
  Save,
  type LucideIcon
};

// Export FontAwesome-named constants for backward compatibility
export const faAdd = Plus;
export const faPlus = Plus;
export const faSolidPlus = Plus;
export const faMinus = Minus;
export const faCheck = Check;
export const faX = X;
export const faXmark = X;
export const faTimes = X;
export const faClose = X;
export const faAngleDown = ChevronDown;
export const faAngleUp = ChevronUp;
export const faChevronDown = ChevronDown;
export const faChevronUp = ChevronUp;
export const faChevronLeft = ChevronLeft;
export const faChevronRight = ChevronRight;
export const faCaretDown = ChevronDown;
export const faCaretUp = ChevronUp;
export const faMenu = Menu;
export const faBars = Menu;
export const faSearch = Search;
export const faSettings = Settings;
export const faCog = Settings;
export const faGear = Settings;
export const faEdit = Edit;
export const faPencil = Edit;
export const faTrash = Trash;
export const faDelete = Trash;
export const faEye = Eye;
export const faEyeSlash = EyeOff;
export const faHome = Home;
export const faUser = User;
export const faFile = File;
export const faFolder = Folder;
export const faImage = Image;
export const faVideo = Video;
export const faPlay = Play;
export const faPause = Pause;
export const faDownload = Download;
export const faUpload = Upload;
export const faCopy = Copy;
export const faSave = Save;
export const faStar = Star;
export const faShare = Share;
export const faVolume = Volume2;
export const faVolumeUp = Volume2;

// Additional commonly used FontAwesome icons
export const faInfoCircle = AlertCircle;
export const faInfo = AlertCircle;
export const faQuestionCircle = HelpCircle;
export const faQuestion = HelpCircle;
export const faExclamationTriangle = AlertTriangle;
export const faTriangleExclamation = AlertTriangle;
export const faExclamation = AlertTriangle;
export const faWarning = AlertTriangle;
export const faBell = Bell;
export const faCalendar = Calendar;
export const faLock = Lock;
export const faUnlock = Unlock;
export const faLockKeyhole = Lock;
export const faGlobe = Globe;
export const faBrowser = Globe;
export const faCode = Edit; // Using Edit as fallback
export const faAtom = Atom;
export const faBug = Bug;
export const faActivity = Activity;
export const faGauge = Gauge;
export const faTachometer = Gauge;
export const faSun = Sun;
export const faMoon = Moon;
export const faZoomIn = ZoomIn;
export const faZoomOut = ZoomOut;
export const faSliders = Sliders;
export const faSlidersH = SlidersHorizontal;
export const faGripVertical = GripVertical;
export const faGripDotsVertical = GripVertical;
export const faMove = Move;
export const faArrowsUpDownLeftRight = Move;
export const faResize = Move;
export const faExpand = Maximize;
export const faCompress = Minimize;
export const faMaximize = Maximize;
export const faMinimize = Minimize;
export const faSquare = Square;
export const faCircle = Circle;
export const faPolygon = Hexagon;
export const faDrawPolygon = Hexagon;
export const faHexagon = Hexagon;
export const faLayers = Layers;
export const faLayerGroup = Layers;
export const faRuler = Ruler;
export const faLink = Link;
export const faCompass = Compass;
export const faGrid2 = Grid3X3;
export const faArchive = Archive;
export const faBackpack = Backpack;
export const faBan = Ban;
export const faClock = Clock;
export const faTime = Clock;
export const faTreePine = TreePine;
export const faTree = TreePine;
export const faRotateLeft = RotateCcw;
export const faUndo = RotateCcw;
export const faArrowLeftRight = ArrowLeftRight;
export const faArrowUpDown = ArrowUpDown;
export const faTrendingUp = TrendingUp;
export const faChart = BarChart3;
export const faBarChart = BarChart3;
export const faCheckCircle = CheckCircle;
export const faCheckCircle2 = CheckCircle2;
export const faCheckSquare = CheckSquare;
export const faSquareCheck = CheckSquare;
export const faSquareXmark = X;
export const faPlusCircle = PlusCircle;
export const faMinusCircle = MinusCircle;
export const faCirclePlus = PlusCircle;
export const faCircleMinus = MinusCircle;
export const faDot = Dot;
export const faCircleDot = Dot;
export const faAt = AtSign;
export const faFolderArrowUp = FolderUp;
export const faFolderUp = FolderUp;
export const faBolt = Zap;
export const faDiceD6 = Dice6;
export const faMinusOctagon = Minus;
export const faMinusSquare = Square;
export const faPlusSquare = Square;
export const faCirclePlay = Play;
export const faArrowLeft = ArrowLeft;
export const faArrowRight = ArrowRight;
export const faArrowUp = ArrowUp;
export const faArrowDown = ArrowDown;
export const faArrowUpRightFromSquare = ExternalLink;
export const faArrowDownToLine = ArrowDownToLine;
export const faCaretCircleDown = ChevronDown;
export const faChevronCircleDown = ChevronDown;
export const faFilter = Filter;
export const faBarsFilter = Filter;
export const faEllipsisV = Menu; // Using Menu as fallback
export const faListUl = Menu; // Using Menu as fallback
export const faPenToSquare = Edit;
export const faPen = Edit;
export const faTimesCircle = X;
export const faCircleXmark = X;
export const faWaveformLines = Activity; // Using Activity as fallback
export const faCloudUpload = Upload;
export const faImages = Image;
export const faFileUpload = Upload;
export const faRedo = RotateCcw; // Using RotateCcw as fallback
export const faCrosshairs = Compass; // Using Compass as fallback
export const faLocationCrosshairs = Compass;

// Additional missing icons
export const faBookmark = Bookmark;
export const faTag = Star; // Using Star as fallback
export const faTags = Star; // Using Star as fallback
export const faShoppingCart = ShoppingCart;
export const faCartPlus = ShoppingCartPlus;
export const faCreditCard = CreditCard;
export const faAlignJustify = AlignJustify;
export const faAlignLeft = AlignJustify;
export const faAlignCenter = AlignJustify;
export const faAlignRight = AlignJustify;
export const faStop = Square;
export const faHeart = Star; // Using Star as fallback
export const faThumbsUp = Star; // Using Star as fallback
export const faDoorOpen = Home; // Using Home as fallback
export const faWalking = User; // Using User as fallback
export const faPersonToPortal = User; // Using User as fallback
export const faRoadBarrier = Ban;
export const faCube = Box;
export const faObjectGroup = Layers; // Using Layers as fallback
export const faRotate = RotateCcw;
export const faPrint = File; // Using File as fallback
export const faWindow = Square; // Using Square as fallback
export const faMobile = User; // Using User as fallback
export const faClone = Copy;
export const faArrowTurnRight = ArrowRight; // Using ArrowRight as fallback
export const faSignalStream = Activity;
export const faArrowDownAZ = ArrowDown; // Using ArrowDown as fallback
export const faArrowUpZA = ArrowUp; // Using ArrowUp as fallback
export const faCircleCheck = CheckCircle;
export const faCircleExclamation = AlertCircle;
export const faLightCalendar = Calendar;
export const faMap = Globe; // Using Globe as fallback
export const faMicrophone = Volume2; // Using Volume2 as fallback
export const faStarCircle = Star;
export const faStarSolid = Star;
export const faFileMagnifyingGlass = Search; // Using Search as fallback
export const faFileExport = Download;
export const faFileImport = Upload;
export const faTrashCan = Trash;
export const faRotateRight = RotateCcw; // Using RotateCcw as fallback for right rotation
export const faDash = Minus; // Using Minus as fallback for dash
export const faTimer = Clock; // Using Clock as fallback for timer
export const faStreetView = Eye; // Using Eye as fallback for street view
export const faTicket = Star; // Using Star as fallback for ticket
export const faPresentationScreen = Square; // Using Square as fallback for presentation screen
export const faPanorama = Image; // Using Image as fallback for panorama
export const faPhotoFilm = Image; // Using Image as fallback for photo film
export const faLamp = Sun; // Using Sun as fallback for lamp
export const faFaceSmileWink = Star; // Using Star as fallback for face smile wink
export const faBook = Book; // Already mapped above but adding for consistency
export const faCartShopping = ShoppingCart; // Already mapped above but adding for consistency
export const faSearchPlus = ZoomIn; // Using ZoomIn as fallback for search plus
export const faSearchMinus = ZoomOut; // Using ZoomOut as fallback for search minus
export const faLinkHorizontal = Link; // Using Link as fallback for horizontal link
export const faUserTie = User; // Using User as fallback for user tie
export const faSignOut = ArrowRight; // Using ArrowRight as fallback for sign out

// Additional missing FontAwesome icons for admin app (non-duplicates only)
export const faAddressCard = User;
export const faAlignSlash = AlignJustify;
export const faArrowsH = ArrowLeftRight;
export const faArrowsRotate = RotateCcw;
export const faArrowsV = ArrowUpDown;
export const faArrowUpFromBracket = ArrowUp;
export const faAtomAlt = Atom;
export const faBanBug = Ban;
export const faBlockQuestion = HelpCircle;
export const faBuilding = Home;
export const faCamera = Image;
export const faChartLine = TrendingUp;
export const faChartMixed = BarChart3;
export const faChartTreeMap = BarChart3;
export const faComment = Star;
export const faComments = Star;
export const faDesktop = Square;
export const faDiceD = Dice6;
export const faEnvelope = Star;
export const faEraser = Trash;
export const faFiles = File;
export const faFolderTimes = Folder;
export const faFolderXmark = Folder;

// More missing icons
export const faG = Globe; // Fixed from earlier
export const faGalleryThumbnails = Image;
export const faGamepadModern = Star;
export const faGrid = Grid3X3;
export const faGripLines = GripVertical;
export const faHandPointer = User;
export const faHashtag = Star;
export const faHeadphones = Volume2;
export const faJoystick = Star;
export const faKeyboard = Star;
export const faLevelUp = ArrowUp;
export const faLightbulb = Sun;
export const faLightPause = Pause;
export const faList = Menu;
export const faLocationDot = Compass;
export const faLongArrowLeft = ArrowLeft;
export const faMagicWandSparkles = Star;
export const faMagnifyingGlassMinus = ZoomOut;
export const faMagnifyingGlassPlus = ZoomIn;
export const faMapMarked = Globe;
export const faMapMarkerAlt = Compass;
export const faMoneyBill = Star;
export const faMusic = Volume2;
export const faPalette = Star;
export const faPaperclip = Star;
export const faPaperPlaneTop = ArrowUp;
export const faPeriod = Dot;
export const faPersonRunning = User;
export const faPersonWalking = User;
export const faPlayCircle = Play;
export const faPuzzle = Star;
export const faRectangleWide = Square;
export const faRotateLeftLight = RotateCcw;
export const faRoute = ArrowRight;

// Final batch of missing icons
export const faScrubber = Sliders;
export const faSealQuestion = HelpCircle;
export const faShareNodes = Share;
export const faSlashForward = ArrowRight;
export const faSmile = Star;
export const faSolidPause = Pause;
export const faSortDown = ArrowDown;
export const faSortUp = ArrowUp;
export const faSpinner = RotateCcw;
export const faSquareDashed = Square;
export const faTachometerAltAverage = Gauge;
export const faTachometerAltFast = Gauge;
export const faTachometerAltFastest = Gauge;
export const faTachometerAltSlow = Gauge;
export const faTh = Grid3X3;
export const faThumbtack = Star;
export const faTilde = Minus;
export const faToggleOff = Settings;
export const faToggleOn = Settings;
export const faTrashLight = Trash;
export const faTriangle = AlertTriangle;
export const faUnlockKeyhole = Unlock;
export const faUsers = User;
export const faV = Check;
export const faVolumeMute = Volume2;
export const faVolumeOff = Volume2;
export const faXmarkCircle = X;
export const faMoneyBill1 = Star; // Using Star as fallback for money bill
export const faDotCircle = Dot; // Using Dot as fallback for dot circle
